import { Dimensions } from 'react-native';

/**
 * Responsive spacing utility for consistent margins, padding, and gaps across screen sizes
 * Based on the typography scaling system but specifically for spacing values
 */

const BASE_WIDTH = 390; // iPhone 13/14 as reference
const MIN_SCALE = 0.75;  // More aggressive scaling for spacing
const MAX_SCALE = 1.15;

// Screen size breakpoints
export const SCREEN_BREAKPOINTS = {
  SMALL: 350,   // Small phones (iPhone SE, etc.)
  MEDIUM: 400,  // Medium phones (iPhone 13/14)
  LARGE: 428,   // Large phones (iPhone 14 Pro Max, etc.)
};

/**
 * Get current screen width
 */
export const getScreenWidth = (): number => {
  return Dimensions.get('window').width;
};

/**
 * Calculate responsive scale factor based on screen width
 */
export const getSpacingScale = (width?: number): number => {
  const screenWidth = width ?? getScreenWidth();
  const scale = screenWidth / BASE_WIDTH;
  return Math.min(Math.max(scale, MIN_SCALE), MAX_SCALE);
};

/**
 * Scale a spacing value responsively
 * @param baseValue - The base spacing value at 390px width
 * @param width - Optional custom width (defaults to current screen width)
 */
export const scale = (baseValue: number, width?: number): number => {
  const scaleFactor = getSpacingScale(width);
  return Math.round(baseValue * scaleFactor);
};

/**
 * Get responsive gap between carousel items
 */
export const getCarouselGap = (width?: number): number => {
  const screenWidth = width ?? getScreenWidth();

  if (screenWidth < SCREEN_BREAKPOINTS.SMALL) {
    return scale(12, screenWidth); // Smaller gap on very small screens
  } else if (screenWidth < SCREEN_BREAKPOINTS.MEDIUM) {
    return scale(16, screenWidth); // Medium gap on typical phones
  } else {
    return scale(20, screenWidth); // Larger gap on bigger screens
  }
};

/**
 * Get responsive horizontal margin for text content
 */
export const getContentMargin = (width?: number): number => {
  const screenWidth = width ?? getScreenWidth();

  if (screenWidth < SCREEN_BREAKPOINTS.SMALL) {
    return scale(12, screenWidth);
  } else if (screenWidth < SCREEN_BREAKPOINTS.MEDIUM) {
    return scale(20, screenWidth);
  } else {
    return scale(30, screenWidth);
  }
};

/**
 * Get responsive side padding for carousel
 */
export const getCarouselSidePadding = (width: number, itemWidth: number): number => {
  const basePadding = (width - itemWidth) / 2;
  const minPadding = scale(12, width);
  return Math.max(basePadding, minPadding);
};

/**
 * Get responsive border radius for cards
 */
export const getCardBorderRadius = (width?: number): number => {
  const screenWidth = width ?? getScreenWidth();

  if (screenWidth < SCREEN_BREAKPOINTS.SMALL) {
    return scale(16, screenWidth);
  } else {
    return scale(24, screenWidth);
  }
};

/**
 * Get responsive border width for cards
 */
export const getCardBorderWidth = (width?: number): number => {
  const screenWidth = width ?? getScreenWidth();

  if (screenWidth < SCREEN_BREAKPOINTS.SMALL) {
    return 2;
  } else {
    return 3;
  }
};

/**
 * Get responsive vertical spacing between carousel and text
 */
export const getCarouselTextSpacing = (width?: number): number => {
  const screenWidth = width ?? getScreenWidth();
  return scale(20, screenWidth);
};

/**
 * Spacing presets for common use cases
 */
export const SPACING = {
  // Use functions to ensure dynamic calculation
  carouselGap: () => getCarouselGap(),
  contentMargin: () => getContentMargin(),
  cardBorderRadius: () => getCardBorderRadius(),
  cardBorderWidth: () => getCardBorderWidth(),
  carouselTextSpacing: () => getCarouselTextSpacing(),
};

export default {
  scale,
  getCarouselGap,
  getContentMargin,
  getCarouselSidePadding,
  getCardBorderRadius,
  getCardBorderWidth,
  getCarouselTextSpacing,
  getSpacingScale,
  getScreenWidth,
  SPACING,
  SCREEN_BREAKPOINTS,
};
